// Aggregates task tags into filter chip options with counts and display labels.
import {
  getDayBoundaries,
  getOverdueTasks,
  getTodayTasks,
} from '@/features/tasks/utils/views/dailySectionsUtils';
import { isUnscheduledCandidate } from '@/features/tasks/utils/views/filtersViewUtils';
import {
  buildTasksMap,
  filterDayWorkUnits,
} from '@/features/tasks/utils/scheduling/taskSchedulingUtils';
import {
  processRootTasks,
  getNextActionsList,
} from '@/features/tasks/utils/views/nextActionUtils';

const DASHBOARD_DAY_RANGE = 8;
const PLAN_DAY_WINDOW = 3;

/** Fired by pages that need attention drawn to the project filter (e.g. Plan modus needs exactly 1 project). */
export const HIGHLIGHT_PROJECT_FILTER_EVENT = 'todoes:highlight-project-filter';

/**
 * @param {string|undefined} pathname
 * @returns {'today'|'plan'|'organize'|'dashboard'}
 */
export function getViewFromPath(pathname = '') {
  if (pathname.startsWith('/app/plan')) return 'plan';
  if (pathname.startsWith('/app/organize')) return 'organize';
  if (pathname.startsWith('/app/dashboard')) return 'dashboard';
  return 'today';
}

const uniqueById = (lists) => {
  const map = new Map();
  lists.flat().forEach((task) => {
    if (task?.id) map.set(task.id, task);
  });
  return Array.from(map.values());
};

const localDateStr = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const taskDateStr = (val) => {
  if (!val) return null;
  const date = val.toDate ? val.toDate() : new Date(val);
  if (Number.isNaN(date.getTime())) return null;
  return localDateStr(date);
};

const scopeTodayTasks = (tasks, { taskTree, tasksMap } = {}) => {
  const { startOfToday, endOfToday } = getDayBoundaries();
  const map = tasksMap || new Map(tasks.map((task) => [task.id, task]));
  const sections = [
    getOverdueTasks(tasks, startOfToday, map),
    getTodayTasks(tasks, startOfToday, endOfToday, map),
  ];
  if (taskTree?.length) {
    const treeMap = new Map(taskTree.map((task) => [task.id, task]));
    const roots = taskTree.filter(
      (task) => !task.parentId || !treeMap.has(task.parentId),
    );
    const nextActionMap = processRootTasks(roots);
    sections.push(
      getNextActionsList(
        map,
        nextActionMap,
        new Set(tasks.map((task) => task.id)),
      ),
    );
  }
  return uniqueById(sections);
};

const scopePlanTasks = (tasks) => {
  const { startOfToday } = getDayBoundaries();
  const startDate = new Date(startOfToday);
  const windowDates = new Set();
  for (let i = 0; i < PLAN_DAY_WINDOW; i += 1) {
    const day = new Date(startDate);
    day.setDate(startDate.getDate() + i);
    windowDates.add(localDateStr(day));
  }

  const unscheduled = (tasks || []).filter(isUnscheduledCandidate);
  const scheduledInWindow = (tasks || []).filter((task) => {
    if (task.completed || task.status === 'Backlog') return false;
    const dateStr = taskDateStr(task.date);
    return dateStr ? windowDates.has(dateStr) : false;
  });

  return uniqueById([unscheduled, scheduledInWindow]);
};

const scopeOrganizeTasks = (tasks) =>
  (tasks || []).filter(
    (task) => !task.completed && !task.parentId && task.status !== 'Backlog',
  );

const scopeDashboardTasks = (tasks, { tasksMap } = {}) => {
  const map = tasksMap?.size ? tasksMap : buildTasksMap(tasks ?? []);
  const allowedDates = new Set();
  const now = new Date();
  for (let i = 0; i < DASHBOARD_DAY_RANGE; i += 1) {
    const day = new Date(now);
    day.setDate(now.getDate() - i);
    allowedDates.add(localDateStr(day));
  }

  const waiting = (tasks || []).filter(
    (task) => !task.completed && task.person && task.status !== 'Backlog',
  );

  const completedByDay = new Map();
  (tasks || []).forEach((task) => {
    if (!task.completed) return;
    const dayStr = taskDateStr(task.date);
    if (!dayStr || !allowedDates.has(dayStr)) return;
    if (!completedByDay.has(dayStr)) completedByDay.set(dayStr, []);
    completedByDay.get(dayStr).push(task);
  });

  const completedWorkUnits = [];
  completedByDay.forEach((dayTasks) => {
    completedWorkUnits.push(...filterDayWorkUnits(dayTasks, map));
  });

  return uniqueById([waiting, completedWorkUnits]);
};

/**
 * Tasks that actually appear in the current page — ignores chip filters so
 * counts stay stable while filtering.
 *
 * @param {Task[]|null|undefined} tasks
 * @param {'today'|'plan'|'organize'|'dashboard'|string} view
 * @param {{ taskTree?: Task[], tasksMap?: Map<string, Task> }} [extras]
 * @returns {Task[]}
 */
export function getViewScopedTasks(tasks, view, extras = {}) {
  if (!tasks) return [];
  switch (view) {
    case 'today':
      return scopeTodayTasks(tasks, extras);
    case 'plan':
      return scopePlanTasks(tasks);
    case 'organize':
      return scopeOrganizeTasks(tasks);
    case 'dashboard':
      return scopeDashboardTasks(tasks, extras);
    default:
      return tasks;
  }
}

/**
 * @param {Task[]|null|undefined} allTasks
 * @param {Map<string,string>} displayMap - lowercase id -> display name
 * @param {(task: Task) => string[]} extractor - getTaskProjects or getTaskContexts
 * @returns {{id: string, label: string, count: number}[]}
 */
export function buildTagOptions(allTasks, displayMap, extractor) {
  if (!allTasks || !extractor) return [];
  const counts = new Map();
  allTasks.forEach((task) => {
    extractor(task)
      .filter(Boolean)
      .forEach((raw) => {
        const id = String(raw).trim();
        if (!id) return;
        const key = id.toLowerCase();
        const prev = counts.get(key);
        counts.set(key, {
          count: (prev?.count || 0) + 1,
          label:
            displayMap && displayMap.has(key)
              ? displayMap.get(key)
              : prev?.id || id,
          id: prev?.id || id,
        });
      });
  });
  return Array.from(counts.values()).sort((a, b) =>
    a.label.toLowerCase().localeCompare(b.label.toLowerCase()),
  );
}

/**
 * Keep selected chips visible even when the current view has zero matching tasks.
 * @param {{id: string, label: string, count: number}[]} options
 * @param {string[]} selectedIds
 * @param {Map<string,string>} [displayMap]
 */
export function buildSelectedFilterSets(projectFilter, contextFilter) {
  return {
    selectedProjects: new Set(projectFilter.map((p) => p.toLowerCase())),
    selectedContexts: new Set(contextFilter.map((c) => c.toLowerCase())),
  };
}

export function toggleFilterById(id, selected, setter) {
  const key = id.toLowerCase();
  setter((prev) =>
    selected.has(key)
      ? prev.filter((x) => x.toLowerCase() !== key)
      : [...prev, id],
  );
}

export function ensureSelectedOptions(options, selectedIds, displayMap) {
  if (!selectedIds?.length) return options;
  const keys = new Set(options.map((opt) => opt.id.toLowerCase()));
  const extras = selectedIds
    .filter((id) => id && !keys.has(id.toLowerCase()))
    .map((id) => {
      const key = id.toLowerCase();
      return {
        id,
        label: displayMap?.get(key) || id,
        count: 0,
      };
    });
  return extras.length ? [...options, ...extras] : options;
}
